import { foldCommonMessage } from './foldCommonMessage'
import type { Edge, Node, Viewport } from '@xyflow/react'
import type {
  ActionNode,
  AverageAdjustment,
  ConditionNode as SchemaConditionNode,
  ConditionParams,
  ConsumptionCondition,
  ChannelContent,
  DaysCondition,
  EmittableCondition,
  EmittableConditionNode,
  EmittableDayConsumptionCondition,
  EmittableDaysCondition,
  EmittableNotificationAction,
  EmittableSeverity,
  EmittableTree,
  Metadata,
  NotificationAction,
  SchemaTree,
} from '@/alerts/treeTypes'
import {
  choiceForOperator,
  operatorForChoice,
} from '@/alerts/comparisonOperators'
import {
  consumptionKindFromCondition,
  consumptionKindToCondition,
} from '@/alerts/consumptionKind'
import {
  consumptionPeriod,
  hourConsumptionCondition,
  hourStreakCondition,
  streakUnit,
  ignoresMissingPeriods,
} from '@/alerts/hourlyAlert'
import {
  cloneCalendar,
  readCalendar,
  type Calendar,
  type DayCalendarWindow,
} from '@/alerts/calendar'

/** UI side node-type discriminator (matches NotificationFlow.tsx nodeTypes map) */
export type UiNodeType =
  | 'connection'
  | 'time'
  | 'consumption'
  | 'meter'
  | 'network'
  | 'message'
  | 'crm'
  | 'end'

// Editor severity comes from each action node's (or the end node's) severityId ('-10'|'0'|'10');
// the emitted outcome takes the max across its actions (most severe wins).
const SEVERITY_VALUES: readonly EmittableSeverity[] = [-10, 0, 10]

function severityFromActionNode(n: Node): EmittableSeverity {
  const raw = Number((n.data as Record<string, unknown>)?.severityId ?? 0)
  if (!SEVERITY_VALUES.includes(raw as EmittableSeverity)) {
    throw new Error(
      `toSchemaTree: invalid severityId "${raw}" on action node ${n.id}`
    )
  }
  return raw as EmittableSeverity
}

// ---------------------------------------------------------------------------
// UI → schema (emit path)
// ---------------------------------------------------------------------------
//
// Each node stores *real backend identifiers* (ConnectNode.attributeName /
// values from the reference bundle; MessageNode.channelIds; MeterNode.code),
// so these mappers read the real fields and emit the real schema shape. Nodes
// deferred to Tier 3 (network condition, crm effect) throw here — the
// completeness layer (Layer A) blocks a save while any deferred node is
// present, so this is only reached as a defensive backstop (caught by Layer B).

type KeptReference = {
  equivalent_period_months?: number
  reference_value?: number
  average_adjustment?: Omit<AverageAdjustment, 'multiplier'>
}

// The node has no control for these stored values, so it carries them in
// `data.kept` and the save writes them back unchanged.
function keptFromUi(raw: unknown): KeptReference {
  if (typeof raw !== 'object' || raw === null) return {}
  return raw as KeptReference
}

function keptFromCondition(c: ConsumptionCondition): KeptReference | null {
  const kept: KeptReference = {}
  if (typeof c.equivalent_period_months === 'number') {
    kept.equivalent_period_months = c.equivalent_period_months
  }
  if (c.average_adjustment) {
    if (typeof c.reference_value === 'number') {
      kept.reference_value = c.reference_value
    }
    const rest: Omit<AverageAdjustment, 'multiplier'> = {
      ...c.average_adjustment,
    }
    delete (rest as AverageAdjustment).multiplier
    if (Object.keys(rest).length > 0) kept.average_adjustment = rest
  }
  return Object.keys(kept).length > 0 ? kept : null
}

// The time node has no control for the weekday fields, which the engine never
// reads. A stored value rides in `data.kept` and the save writes it back.
type TimeKept = Pick<DaysCondition, 'consider_days' | 'consider_shabbat'>

function keptFromDays(c: DaysCondition): TimeKept | null {
  const kept: TimeKept = {}
  if (Array.isArray(c.consider_days) && c.consider_days.length > 0) {
    kept.consider_days = c.consider_days
  }
  if (c.consider_shabbat !== undefined)
    kept.consider_shabbat = c.consider_shabbat
  return Object.keys(kept).length > 0 ? kept : null
}

const BOUND_FIELDS = [
  ['minValue', 'min_value'],
  ['maxValue', 'max_value'],
] as const

function uiToCondition(node: Node): EmittableCondition {
  const t = node.type as UiNodeType
  const d = (node.data ?? {}) as Record<string, unknown>
  switch (t) {
    case 'connection': {
      const values = Array.isArray(d.values) ? d.values.map(String) : []
      // The pickers hold strings, but a tree stored with numbers must keep them.
      const asNumbers =
        d.numericValuesFor !== undefined &&
        d.numericValuesFor === d.attributeName &&
        values.every(v => v.trim() !== '' && Number.isFinite(Number(v)))
      return {
        kind: 'attribute',
        attribute: String(d.attributeName ?? ''),
        values: asNumbers ? values.map(Number) : values,
      }
    }
    case 'time': {
      if (streakUnit(d) === 'hours') return hourStreakCondition(d)
      const cond: EmittableDaysCondition = {
        kind: 'days',
        days: Number(d.value),
      }
      if (ignoresMissingPeriods(d)) cond.ignore_missing_days = true
      const kept = d.kept as TimeKept | undefined
      if (kept?.consider_days) cond.consider_days = kept.consider_days
      if (kept?.consider_shabbat !== undefined) {
        cond.consider_shabbat = kept.consider_shabbat
      }
      return cond
    }
    case 'consumption': {
      const operator = operatorForChoice(String(d.secondSelectedId ?? '1'))
      const unit = String(d.thirdSelectedId ?? '2') // 1=%, 2=m3, 3=liters
      const calendar = d.calendar as Calendar | undefined
      if (consumptionPeriod(d) === 'hour') {
        const hourCond = hourConsumptionCondition(d, node.id, operator)
        if (calendar) hourCond.calendar = cloneCalendar(calendar)
        return hourCond
      }
      const cond: EmittableDayConsumptionCondition = {
        kind: 'consumption',
        ...consumptionKindToCondition(d, node.id),
        operator,
      }
      // Average sources. Schema resolution priority is equivalent-period →
      // recent-months → fixed reference_value, so emitting several sources is
      // well-defined; the checkbox caption pins the recent-3 fallback.
      const kept = keptFromUi(d.kept)
      if (d.secondChecked) {
        cond.equivalent_period_months =
          d.equivalentMonths !== undefined
            ? Number(d.equivalentMonths)
            : (kept.equivalent_period_months ?? 1)
        // A stored equivalent period has no fallback unless the tree names one.
        if (!d.firstChecked && kept.equivalent_period_months === undefined) {
          cond.recent_months = 3
        }
      }
      if (d.firstChecked) {
        // Layer A guarantees basis=average, unit=months, positive N.
        cond.recent_months = Number(d.secondValue)
      }
      const value = Number(d.value)
      if (unit === '1') {
        // Percent of the average reference. The schema forbids
        // average_adjustment without an average source; Layer A blocks this
        // upstream — throwing here is the Layer-B backstop.
        if (!d.firstChecked && !d.secondChecked) {
          throw new Error(
            `uiToCondition: percent threshold on ${node.id} requires an average source`
          )
        }
        const adjustment: AverageAdjustment = {
          multiplier: value / 100,
          ...kept.average_adjustment,
        }
        // The node edits these two bounds, so its text wins over a stored one.
        for (const [field, key] of BOUND_FIELDS) {
          const raw = d[field]
          if (raw === undefined) continue
          delete adjustment[key]
          if (String(raw).trim() !== '') adjustment[key] = Number(raw)
        }
        cond.average_adjustment = adjustment
        if (kept.reference_value !== undefined) {
          cond.reference_value = kept.reference_value
        }
      } else {
        cond.reference_value = unit === '3' ? value / 1000 : value
      }
      if (calendar) cond.calendar = dayCalendar(calendar, node.id)
      return cond
    }
    case 'meter':
      // Real device-alert mapping (T6). `code` holds a real code from the
      // reference bundle; Layer A blocks the save while it is empty.
      return { kind: 'device_alert', code: String(d.code ?? '') }
    case 'network':
      throw new Error(
        `uiToCondition: node type "${t}" is not yet supported (Tier 3)`
      )
    default:
      throw new Error(`uiToCondition: unsupported node type ${t}`)
  }
}

// Layer A blocks hours on a day check; throwing here is the Layer-B backstop.
function dayCalendar(cal: Calendar, nodeId: string): DayCalendarWindow[] {
  if (cal.some(w => w.hours !== undefined || w.weekdays === undefined)) {
    throw new Error(`uiToCondition: day check ${nodeId} has calendar hours`)
  }
  return cloneCalendar(cal as DayCalendarWindow[])
}

/** "Open for" unit options of the CRM node; ids are what the node stores in `firstSelectedId`. */
export const CRM_OPEN_FOR_UNIT_OPTIONS = [
  { id: '1', label: 'ימים' },
  { id: '2', label: 'שבועות' },
  { id: '3', label: 'חודשים' },
]
const CRM_OPEN_FOR_ID_BY_UNIT: Record<string, string> = {
  days: '1',
  weeks: '2',
  months: '3',
}

/** Task type names of the CRM node's old hard-coded list, keyed by the ids it stored. */
const LEGACY_CRM_TASK_TYPES: Record<string, string> = {
  '1': 'בירור',
  '2': 'פנייה',
}

export function legacyCrmTaskType(secondSelectedId: unknown): string | null {
  return secondSelectedId != null
    ? (LEGACY_CRM_TASK_TYPES[String(secondSelectedId)] ?? null)
    : null
}

const BLOCK_SHABBAT_AND_FESTIVAL = {
  shabbat: { block_shabbat: true, block_festival: true },
}

// The checkbox covers exactly "block both" and "block neither". Any other
// stored policy has no control, so it rides in `data.keptRunTimePolicy` and
// the save writes it back unchanged until the user ticks the box.
function shabbatFromPolicy(policy: unknown): {
  blockShabbat: boolean
  kept?: unknown
} {
  if (policy === undefined || policy === 'default') {
    return { blockShabbat: false }
  }
  if (typeof policy === 'object' && policy !== null) {
    const keys = Object.keys(policy)
    const shabbat = (policy as { shabbat?: unknown }).shabbat
    if (
      keys.length === 1 &&
      keys[0] === 'shabbat' &&
      typeof shabbat === 'object' &&
      shabbat !== null
    ) {
      const flags = shabbat as Record<string, unknown>
      const onlyFlags = Object.keys(flags).every(
        k => k === 'block_shabbat' || k === 'block_festival'
      )
      const shabbatBlocked = flags.block_shabbat === true
      const festivalBlocked = flags.block_festival === true
      const bothSet = Object.keys(flags).length === 2
      if (onlyFlags && shabbatBlocked && festivalBlocked && bothSet) {
        return { blockShabbat: true }
      }
      const noneBlocked = Object.values(flags).every(v => v === false)
      if (onlyFlags && noneBlocked) return { blockShabbat: false }
    }
  }
  return { blockShabbat: false, kept: policy }
}

function runTimePolicyFromUi(d: Record<string, unknown>) {
  if (d.blockShabbat === true) return BLOCK_SHABBAT_AND_FESTIVAL
  return d.keptRunTimePolicy
}

// A tree names channels by key, so a node with its own channel texts is emitted
// with channel keys. That needs a key for every selected channel; without one
// the node keeps its ids and the texts cannot be written.
function channelsWithTexts(
  d: Record<string, unknown>,
  ids: number[],
  channelIdsByKey: ChannelIdsByKey
) {
  const content = d.channelContent
  if (!content || typeof content !== 'object') return undefined
  const keyById = new Map(
    [...channelIdsByKey].map(([key, id]) => [id, key] as const)
  )
  const keys = ids.map(id => keyById.get(id))
  if (ids.length === 0 || keys.some(key => key === undefined)) return undefined
  const own = Object.entries(content as ChannelContent).filter(([key]) =>
    keys.includes(key)
  )
  return own.length > 0
    ? { keys: keys as string[], content: Object.fromEntries(own) }
    : undefined
}

function uiToAction(
  node: Node,
  channelIdsByKey: ChannelIdsByKey
): EmittableNotificationAction {
  const t = node.type as UiNodeType
  const d = (node.data ?? {}) as Record<string, unknown>
  const meta = {
    layout: { x: node.position.x, y: node.position.y },
    uiType: t,
    uiData: d,
  }
  if (t === 'message') {
    const ids = Array.isArray(d.channelIds)
      ? d.channelIds.map(Number).filter(n => Number.isFinite(n))
      : []
    const runTimePolicy = runTimePolicyFromUi(d)
    const texts = channelsWithTexts(d, ids, channelIdsByKey)
    // The schema and the engine demand a non-blank `message`. The author writes
    // texts per channel only, so the first ticked channel's body fills it, as
    // the send window does. Every ticked channel has its own body (completeness
    // checks that), so no channel ever sends this one. The key is omitted when
    // empty so the schema's pattern never sees a blank string.
    const common = typeof d.messageBody === 'string' ? d.messageBody.trim() : ''
    const firstBody = texts
      ? (texts.content[texts.keys[0]]?.body?.trim() ?? '')
      : ''
    const body = common !== '' ? common : firstBody
    return {
      id: node.id,
      node_type: 'notification',
      trigger: { kind: 'on_open' },
      ...(texts
        ? {
            notification_channel_keys: texts.keys,
            channel_content: texts.content,
          }
        : { notification_channel_ids: ids }),
      ...(body !== '' ? { message: body } : {}),
      ...(runTimePolicy !== undefined
        ? { run_time_policy: runTimePolicy }
        : {}),
      metadata: meta,
    }
  }
  if (t === 'crm') {
    throw new Error('uiToAction: CRM node is not yet supported (Tier 3)')
  }
  throw new Error(`uiToAction: not an action node ${t}`)
}

const ACTION_TYPES: ReadonlySet<UiNodeType> = new Set(['message', 'crm'])
const CONDITION_TYPES: ReadonlySet<UiNodeType> = new Set([
  'connection',
  'time',
  'consumption',
  'meter',
  'network',
])

/**
 * What a stored tree holds that the canvas has no node for. Saving writes it back, so an author who
 * edits one value does not lose `metadata.code`, `metadata.name_he` or the ids a stored tree was given.
 */
export type KeptFromStored = {
  /** The stored top-level metadata without `ui`, which the editor owns. */
  metadata: Metadata
  /** Outcome ids by the id of the condition that holds the outcome. */
  outcomeIds: Record<string, string>
  /** The tree calendar as stored. The editor seeds its own state from it. */
  calendar?: Calendar
}

export function toSchemaTree(
  nodes: Node[],
  edges: Edge[],
  rootId: string,
  viewport: Viewport,
  kept?: KeptFromStored,
  treeCalendar?: Calendar,
  channelIdsByKey: ChannelIdsByKey = new Map()
): EmittableTree {
  const nodeById = new Map(nodes.map(n => [n.id, n]))
  const childrenByParent = new Map<string, string[]>()
  for (const e of edges) {
    const list = childrenByParent.get(e.source) ?? []
    list.push(e.target)
    childrenByParent.set(e.source, list)
  }

  function buildCondition(id: string): EmittableConditionNode {
    const n = nodeById.get(id)
    if (!n) throw new Error(`toSchemaTree: node ${id} not found`)
    const t = n.type as UiNodeType
    if (!CONDITION_TYPES.has(t)) {
      throw new Error(
        `toSchemaTree: expected condition-like node at ${id}, got ${t}`
      )
    }
    const kidIds = childrenByParent.get(id) ?? []
    const childNodes: EmittableConditionNode[] = []
    const actionNodes: EmittableNotificationAction[] = []
    const actionRfNodes: Node[] = []
    for (const cid of kidIds) {
      const c = nodeById.get(cid)
      if (!c) continue
      const ct = c.type as UiNodeType
      if (CONDITION_TYPES.has(ct)) childNodes.push(buildCondition(cid))
      else if (ACTION_TYPES.has(ct)) {
        actionNodes.push(uiToAction(c, channelIdsByKey))
        actionRfNodes.push(c)
      }
    }

    // Structural invariant: an outcome-bearing condition node must have no
    // condition children. The schema only permits observer children alongside
    // an outcome, and the editor never emits observers — so both together is
    // always schema-invalid. (Also enforced at authoring time by the onConnect
    // guard in NotificationFlow.tsx.)
    if (childNodes.length > 0 && actionNodes.length > 0) {
      throw new Error(
        `toSchemaTree: node ${id} has both condition children and action ` +
          `children; an outcome-bearing condition node must have no condition children`
      )
    }

    const result: EmittableConditionNode = {
      id,
      node_type: 'condition',
      condition: uiToCondition(n),
      // metadata.layout drives node position; metadata.uiData carries the full
      // editor state so reopening the diagram restores every field. Engine
      // ignores metadata.
      metadata: {
        layout: { x: n.position.x, y: n.position.y },
        uiType: n.type,
        uiData: n.data ?? {},
      },
    }
    if (childNodes.length > 0) result.children = childNodes
    const endNode = kidIds
      .map(cid => nodeById.get(cid))
      .find(c => c?.type === 'end')
    if (actionNodes.length > 0) {
      result.outcome = {
        id: kept?.outcomeIds[id] ?? `${id}-outcome`,
        node_type: 'outcome',
        severity: actionRfNodes
          .map(severityFromActionNode)
          .reduce<EmittableSeverity>(
            (acc, s) => (s > acc ? s : acc),
            -10 as EmittableSeverity
          ),
        actions: actionNodes,
        metadata: { layout: { x: n.position.x + 300, y: n.position.y } },
      }
    } else if (endNode) {
      result.outcome = {
        id: endNode.id,
        node_type: 'outcome',
        severity: severityFromActionNode(endNode),
        actions: [],
        metadata: {
          layout: { x: endNode.position.x, y: endNode.position.y },
          uiType: 'end',
          uiData: endNode.data ?? {},
        },
      }
    }
    return result
  }

  return {
    schema_version: 1,
    metadata: {
      ...kept?.metadata,
      ui: {
        viewport,
        edges: edges.map(e => ({
          id: e.id,
          source: e.source,
          target: e.target,
          type: e.type ?? 'smoothstep',
        })),
      },
    },
    ...(treeCalendar ? { calendar: cloneCalendar(treeCalendar) } : {}),
    root: buildCondition(rootId),
  }
}

// ---------------------------------------------------------------------------
// schema → UI (read path)
// ---------------------------------------------------------------------------

type UiMeta = {
  layout?: { x?: number; y?: number }
  uiType?: string
  uiData?: Record<string, unknown>
}

/**
 * Thrown by `fromSchemaTree` for a tree the editor would change on save: a
 * channel key that maps to no channel id, or the `default` channel, which has
 * no id to hold and which the engine refuses at publish.
 */
export class UnsupportedTreeError extends Error {
  constructor(readonly reasons: string[]) {
    super(
      `לא ניתן לפתוח את ההתראה לעריכה, כי שמירה מהעורך הייתה משנה את משמעותה. ${reasons.join('. ')}.`
    )
    this.name = 'UnsupportedTreeError'
  }
}

const REFERENCE_FIELDS = ['equivalent_period_months', 'recent_months'] as const

/** Channel ids by the key a tree written outside the editor may name them with. */
export type ChannelIdsByKey = ReadonlyMap<string, number>

export function fromSchemaTree(
  tree: SchemaTree,
  channelIdsByKey: ChannelIdsByKey = new Map(),
  // Every channel the Gateway lists, disabled ones too. Undefined when the list
  // is unknown, and then ids are not checked.
  knownChannelIds?: ReadonlySet<number>
): {
  nodes: Node[]
  edges: Edge[]
  viewport: Viewport
  rootId: string
  kept: KeptFromStored
} {
  const nodes: Node[] = []
  const edges: Edge[] = []
  const unsupported: string[] = []
  const outcomeIds: Record<string, string> = {}
  // Nodes of a tree written outside the editor store no layout.
  const unplaced = new Set<string>()

  function emitCondition(n: SchemaConditionNode, parentId?: string): void {
    const meta = (n.metadata ?? {}) as UiMeta
    const layout = meta.layout ?? { x: 0, y: 0 }
    const nodeId = n.id ?? 'root'
    if (!meta.layout) unplaced.add(nodeId)
    // Prefer the round-tripped UI state if present (metadata.uiData /
    // metadata.uiType). Falls back to deriving from condition.* for trees not
    // authored by this editor.
    const savedUiData = meta.uiData
    const savedUiType = meta.uiType
    if (!savedUiData) {
      for (const field of REFERENCE_FIELDS) {
        const stored = (n.condition as Record<string, unknown> | undefined)?.[
          field
        ]
        if (stored === 'default') {
          unsupported.push(
            `התנאי "${nodeId}" משתמש ב-default בשדה ${field}, והעורך אינו יודע לשמור אותו`
          )
        }
      }
    }
    // isDefault:true tells node components to initialize their local state
    // from data instead of fallback empties.
    const data: Record<string, unknown> = savedUiData
      ? { ...savedUiData, ...keptOfSavedTime(n.condition), isDefault: true }
      : { ...conditionToUiData(n.condition), isDefault: true }
    // The stored calendar is the truth; uiData may hold a stale one.
    delete data.calendar
    const storedCalendar = (n.condition as { calendar?: unknown } | undefined)
      ?.calendar
    if (storedCalendar !== undefined) {
      const calendar = readCalendar(storedCalendar)
      if (n.condition.kind !== 'consumption') {
        unsupported.push(
          `התנאי "${nodeId}" מסוג ${n.condition.kind} מגדיר לוח זמנים, וסוג זה אינו תומך בלוח זמנים`
        )
      } else if (!calendar) {
        unsupported.push(`לוח הזמנים של התנאי "${nodeId}" אינו תקין`)
      } else {
        data.calendar = calendar
      }
    }
    nodes.push({
      id: nodeId,
      type: savedUiType ?? conditionKindToUiType(n.condition?.kind),
      position: { x: Number(layout.x ?? 0), y: Number(layout.y ?? 0) },
      data,
    })
    if (parentId) {
      edges.push({
        id: `e-${parentId}-${nodeId}`,
        source: parentId,
        target: nodeId,
        type: 'smoothstep',
      })
    }
    for (const c of n.children ?? []) {
      if (c.node_type === 'condition') emitCondition(c, nodeId)
    }
    if (n.outcome) {
      if (n.outcome.metadata?.uiType !== 'end') {
        outcomeIds[nodeId] = n.outcome.id
      }
      for (const a of n.outcome.actions ?? []) {
        const aMeta = (a.metadata ?? {}) as UiMeta
        const al = aMeta.layout ?? { x: 0, y: 0 }
        const actionId = a.id ?? `${nodeId}-action`
        if (!aMeta.layout) unplaced.add(actionId)
        const aSavedUiData = aMeta.uiData
        const aSavedUiType = aMeta.uiType
        let keyedChannelIds: string[] | undefined
        if (a.node_type === 'notification') {
          const keys = (a as { notification_channel_keys?: unknown })
            .notification_channel_keys
          const ids = (a as { notification_channel_ids?: unknown })
            .notification_channel_ids
          if (Array.isArray(keys)) {
            const unmapped = keys.filter(k => !channelIdsByKey.has(String(k)))
            if (unmapped.length > 0) {
              unsupported.push(
                `פעולת השליחה "${actionId}" מציינת ערוצים לפי מפתח שאינו מתאים לאף ערוץ (${unmapped.join(', ')})`
              )
            } else {
              keyedChannelIds = keys.map(k =>
                String(channelIdsByKey.get(String(k)))
              )
            }
          } else if (Array.isArray(ids) && knownChannelIds) {
            const unknown = ids.filter(i => !knownChannelIds.has(Number(i)))
            if (unknown.length > 0) {
              unsupported.push(
                `פעולת השליחה "${actionId}" מציינת ערוצים לפי מזהה שאינו מתאים לאף ערוץ (${unknown.join(', ')})`
              )
            }
          } else if (ids === 'default') {
            unsupported.push(
              `פעולת השליחה "${actionId}" משתמשת בערוץ ברירת מחדל, והעורך שומר ערוצים לפי מזהה בלבד`
            )
          }
        }
        // like severityId, the stored texts are canonical: stale editor data goes
        const savedUiData = Object.fromEntries(
          Object.entries(aSavedUiData ?? {}).filter(
            ([key]) => key !== 'channelContent'
          )
        )
        const storedContent =
          a.node_type === 'notification' ? a.channel_content : undefined
        if (storedContent && !keyedChannelIds) {
          unsupported.push(
            `פעולת השליחה "${actionId}" כוללת נוסח לפי ערוץ אך מציינת ערוצים לפי מזהה, והעורך שומר נוסח לפי ערוץ רק בצומת שערוציו נקראים לפי מפתח`
          )
        }
        const channelContent = storedContent
        nodes.push({
          id: actionId,
          type: aSavedUiType ?? (a.node_type === 'effect' ? 'crm' : 'message'),
          position: { x: Number(al.x ?? 0), y: Number(al.y ?? 0) },
          // severityId is canonical (outcome.severity) — it must win over
          // stale uiData when a tree was edited outside this editor.
          data: foldCommonMessage(
            {
              ...(aSavedUiData ? savedUiData : actionToUiData(a)),
              ...(keyedChannelIds ? { channelIds: keyedChannelIds } : {}),
              ...(channelContent ? { channelContent } : {}),
              ...(a.node_type === 'notification' ? shabbatUiData(a) : {}),
              severityId: String(n.outcome?.severity ?? 0),
              isDefault: true,
            },
            channelIdsByKey
          ),
        })
        edges.push({
          id: `e-${nodeId}-${actionId}`,
          source: nodeId,
          target: actionId,
          type: 'smoothstep',
        })
      }
      const outcomeMeta = (n.outcome.metadata ?? {}) as UiMeta
      if (outcomeMeta.uiType === 'end') {
        const layout = outcomeMeta.layout ?? { x: 0, y: 0 }
        if (!outcomeMeta.layout) unplaced.add(n.outcome.id)
        nodes.push({
          id: n.outcome.id,
          type: 'end',
          position: { x: Number(layout.x ?? 0), y: Number(layout.y ?? 0) },
          // Same rule as the send node: outcome.severity is canonical. The
          // legacy 5-level `severity` choice never reached the engine.
          data: {
            ...(outcomeMeta.uiData ?? {}),
            severityId: String(n.outcome.severity),
            isDefault: true,
          },
        })
        edges.push({
          id: `e-${n.id}-${n.outcome.id}`,
          source: nodeId,
          target: n.outcome.id,
          type: 'smoothstep',
        })
      }
    }
  }
  emitCondition(tree.root)
  const treeCalendar =
    tree.calendar === undefined ? undefined : readCalendar(tree.calendar)
  if (treeCalendar === null) unsupported.push('לוח הזמנים של העץ אינו תקין')
  if (unsupported.length > 0) throw new UnsupportedTreeError(unsupported)

  const { ui: storedUi, ...keptMetadata } = (tree.metadata ?? {}) as {
    ui?: { viewport?: Viewport; edges?: Edge[] }
  } & Metadata
  const ui = storedUi ?? {}
  const viewport: Viewport = ui.viewport ?? { x: 0, y: 0, zoom: 1 }
  if (Array.isArray(ui.edges) && ui.edges.length > 0) {
    edges.splice(0, edges.length, ...ui.edges)
  }
  layOut(nodes, edges, tree.root.id ?? 'root', unplaced)

  return {
    nodes,
    edges,
    viewport,
    rootId: tree.root.id ?? 'root',
    kept: {
      metadata: keptMetadata,
      outcomeIds,
      ...(treeCalendar ? { calendar: treeCalendar } : {}),
    },
  }
}

// A node is up to 548 wide and 380 tall, so a row and a column leave a gap.
const ROW_HEIGHT = 440
const COLUMN_WIDTH = 620

/**
 * Puts the nodes that have no stored layout in rows by depth below the root,
 * the nodes of one row side by side around x = 0.
 */
function layOut(
  nodes: Node[],
  edges: Edge[],
  rootId: string,
  unplaced: ReadonlySet<string>
): void {
  if (unplaced.size === 0) return
  const depth = new Map([[rootId, 0]])
  const queue = [rootId]
  while (queue.length > 0) {
    const id = queue.shift()!
    for (const e of edges) {
      if (e.source === id && !depth.has(e.target)) {
        depth.set(e.target, depth.get(id)! + 1)
        queue.push(e.target)
      }
    }
  }
  const rows = new Map<number, Node[]>()
  for (const n of nodes) {
    if (!unplaced.has(n.id)) continue
    const d = depth.get(n.id) ?? 0
    rows.set(d, [...(rows.get(d) ?? []), n])
  }
  for (const [d, row] of rows) {
    row.forEach((n, i) => {
      n.position = {
        x: (i - (row.length - 1) / 2) * COLUMN_WIDTH,
        y: d * ROW_HEIGHT,
      }
    })
  }
}

/**
 * Whether two serialized trees say the same, so that saving the second would publish nothing new. The
 * viewport is where the author looked last; it says nothing about the alert.
 */
export function sameTree(a: unknown, b: unknown): boolean {
  return canonical(withoutViewport(a)) === canonical(withoutViewport(b))
}

function withoutViewport(tree: unknown): unknown {
  const t = tree as { metadata?: { ui?: Record<string, unknown> } } | null
  if (!t?.metadata?.ui || !('viewport' in t.metadata.ui)) return tree
  const ui = { ...t.metadata.ui }
  delete ui.viewport
  return { ...t, metadata: { ...t.metadata, ui } }
}

// JSON text with the keys sorted, so key order does not count as a change.
function canonical(value: unknown): string {
  return JSON.stringify(value, (_key, v) =>
    v && typeof v === 'object' && !Array.isArray(v)
      ? Object.fromEntries(
          Object.entries(v as Record<string, unknown>).sort(([x], [y]) =>
            x < y ? -1 : 1
          )
        )
      : v
  )
}

function conditionKindToUiType(kind: string | undefined): UiNodeType {
  switch (kind) {
    case 'attribute':
    case 'group':
    case 'hierarchy':
    case 'zone_role':
      return 'connection'
    case 'days':
    case 'streak':
      return 'time'
    case 'consumption':
      return 'consumption'
    case 'device_alert':
      return 'meter'
    default:
      return 'connection'
  }
}

// Editor state saved before the weekdays control went holds them as
// weekDaysChecked and option2SelectedId. The stored condition is the truth.
function keptOfSavedTime(c: ConditionParams | undefined) {
  const kept = c?.kind === 'days' ? keptFromDays(c) : null
  return kept ? { kept } : {}
}

// Fallback UI-state derivation for trees not authored by this editor (no
// metadata.uiData). Uses the *new* node field names; labels are re-derived by
// the node components from the reference bundle at render time.
function conditionToUiData(c: ConditionParams): Record<string, unknown> {
  switch (c?.kind) {
    case 'attribute': {
      const values = c.values ?? []
      return {
        attributeName: c.attribute,
        values: values.map(String),
        ...(values.length > 0 && values.every(v => typeof v === 'number')
          ? { numericValuesFor: c.attribute }
          : {}),
      }
    }
    case 'days': {
      const kept = keptFromDays(c)
      return {
        value: c.days,
        ...(kept ? { kept } : {}),
        ...(typeof c.ignore_missing_days === 'boolean'
          ? { ignoreMissingPeriods: c.ignore_missing_days }
          : {}),
      }
    }
    case 'streak':
      return {
        unitId: c.period === 'hour' ? 'hours' : 'days',
        value: c.count,
        ...(typeof c.ignore_missing_periods === 'boolean'
          ? { ignoreMissingPeriods: c.ignore_missing_periods }
          : {}),
      }
    case 'consumption': {
      const ui: Record<string, unknown> = {
        ...consumptionKindFromCondition(c),
        ...(c.period === 'hour' ? { periodId: 'hour' } : {}),
        secondSelectedId: choiceForOperator(c.operator),
      }
      if (c.average_adjustment) {
        ui.thirdSelectedId = '1' // %
        // An absent multiplier means 1.
        const multiplier = c.average_adjustment.multiplier ?? 1
        ui.value = Number((multiplier * 100).toPrecision(12))
        for (const [field, key] of BOUND_FIELDS) {
          const bound = c.average_adjustment[key]
          if (bound !== undefined) ui[field] = bound
        }
      } else {
        // Canonical storage is m³ — liters never round-trips (uiData covers
        // the normal editor flow; this path is for hand-edited trees).
        ui.thirdSelectedId = '2'
        ui.value =
          typeof c.reference_value === 'number' ? c.reference_value : ''
      }
      if (typeof c.recent_months === 'number') {
        ui.firstChecked = true
        ui.fourthSelectedId = '1' // average basis
        ui.fifthSelectedId = '2' // months
        ui.secondValue = c.recent_months
      }
      if (typeof c.equivalent_period_months === 'number') {
        ui.secondChecked = true
        ui.equivalentMonths = c.equivalent_period_months
      }
      const kept = keptFromCondition(c)
      if (kept) ui.kept = kept
      return ui
    }
    case 'device_alert':
      return { code: c.code }
    default:
      return {}
  }
}

// Like severityId, the stored policy is canonical and must win over stale
// editor data in the action's metadata.
function shabbatUiData(a: NotificationAction) {
  const { blockShabbat, kept } = shabbatFromPolicy(a.run_time_policy)
  return {
    blockShabbat,
    keptRunTimePolicy: kept,
  }
}

function actionToUiData(a: ActionNode): Record<string, unknown> {
  if (a.node_type === 'notification') {
    const ids =
      'notification_channel_ids' in a &&
      Array.isArray(a.notification_channel_ids)
        ? a.notification_channel_ids.map(String)
        : []
    const ui: Record<string, unknown> = { channelIds: ids }
    if (typeof a.message === 'string') ui.messageBody = a.message
    return ui
  }
  const p = a.parameters ?? {}
  return {
    taskType: p.taskType ?? null,
    assigneeId: p.assignee != null ? String(p.assignee) : null,
    firstValue: p.openForValue != null ? String(p.openForValue) : '',
    firstSelectedId:
      typeof p.openForUnit === 'string'
        ? (CRM_OPEN_FOR_ID_BY_UNIT[p.openForUnit] ?? null)
        : null,
  }
}
